/**
 * Layout editor ↔ 3D preview axis contract
 *
 *   Editor canvas (image):  x right,  y down
 *   Draft / DB layout feet: x right,  z south (= image y)
 *   Preview Three.js:       x right,  y up,    z south (= layout z, NOT negated)
 *   Site 3D (production):   x right,  y up,    -z south
 */
import * as THREE from "three";

/** Site placeholder vs drei OrbitControls — plain {x,y,z} or THREE.Vector3. */
const setVector3Like = (v, x, y, z) => {
    if (!v) return;
    if (typeof v.set === "function") {
        v.set(x, y, z);
    } else {
        v.x = x;
        v.y = y;
        v.z = z;
    }
};

/** Match production Site 3D: currentLookAt, target, optional updateCamera. */
const syncOrbitControlsLookAt = (controls, x, y, z) => {
    if (!controls) return;
    if (controls.currentLookAt) {
        setVector3Like(controls.currentLookAt, x, y, z);
    }
    if (controls.target) {
        if (
            typeof controls.target.copy === "function" &&
            controls.currentLookAt
        ) {
            controls.target.copy(controls.currentLookAt);
        } else {
            setVector3Like(controls.target, x, y, z);
        }
    }
    if (typeof controls.updateCamera === "function") {
        controls.updateCamera(new THREE.Vector3(x, y, z));
    }
};

const setEulerLike = (e, x, y, z, order) => {
    if (!e) return;
    if (typeof e.set === "function") {
        e.set(x, y, z, order ?? e.order);
    } else {
        e.x = x;
        e.y = y;
        e.z = z;
        if (order != null) e.order = order;
    }
};

/** Image/layout Y (down on canvas) → layout feet Z (stored in draft). */
export const layoutImageYToLayoutZ = (imageY) => imageY || 0;

/** Layout feet Z → preview world Z (1:1 with editor; Site3d uses -z). */
export const layoutPreviewWorldZ = (layoutZ) => layoutZ || 0;

/** Site Layout preview / editor-saved layout — +Z like canvas (legacy manual layouts use -Z). */
export const siteUsesEditorLayoutAxes = (site) =>
    !!(
        site?.isLayoutPreview ||
        site?.isLayoutPreviewSite ||
        site?.isLayoutPreview3d ||
        site?.layoutUsesEditorLayoutAxes
    );

/** Editor enclosure polygon (bin-local feet); no transform when axes already match canvas. */
export const previewPolygonFtForSite = (site, polyFt) => polyFt;

/** Layout Z → Three.js world Z for the given site view mode. */
export const layoutWorldZForSite = (site, layoutZ) =>
    siteUsesEditorLayoutAxes(site) ? layoutPreviewWorldZ(layoutZ) : -(layoutZ || 0);

/** Layout feet (x, z) → preview world position [x, yUp, z]. */
export const layoutToPreviewWorld = (layoutX, layoutUpY, layoutZ) => [
    layoutX || 0,
    layoutUpY || 0,
    layoutPreviewWorldZ(layoutZ),
];

/** Collect layout-foot centers (group + bin) from a draft. */
export const layoutDraftFootprintPoints = (draft) => {
    const pts = [];
    (draft?.groups || []).forEach((g) => {
        (g.bins || []).forEach((b) => {
            const x = (g.x || 0) + (b.x || 0);
            const z = (g.z || 0) + (b.z || 0);
            if (Number.isFinite(x) && Number.isFinite(z)) pts.push({ x, z });
        });
    });
    return pts;
};

/**
 * Top-down plan camera: +X screen-right, +Z screen-down (matches canvas y-down).
 * @param {object} viewport `{ width, height }` from R3F `useThree().size`
 * @returns {boolean}
 */
/** Match 2D layout editor zoom (fitZoom / viewZoom) and pan focus. */
const previewLookAtFromEditorView = (bboxCenter, editorView) => {
    const look = {
        x: bboxCenter.x,
        y: bboxCenter.y,
        z: bboxCenter.z,
    };
    if (
        !editorView?.feetPerPixel ||
        !editorView?.siteCenterImage ||
        editorView.focusImageX == null ||
        editorView.focusImageY == null
    ) {
        return look;
    }
    const fpp = editorView.feetPerPixel;
    const sc = editorView.siteCenterImage;
    look.x = (editorView.focusImageX - sc.x) * fpp;
    look.y = 0;
    look.z = layoutPreviewWorldZ((editorView.focusImageY - sc.y) * fpp);
    return look;
};

/**
 * Orthographic half-extents (layout feet) from the 2D editor viewport — matches canvas zoom/pan.
 */
export const layoutPreviewFrustumFromEditorView = (editorView) => {
    if (!(editorView?.feetPerPixel > 0)) return null;
    const canvasW = Math.max(
        1,
        (editorView.baseWidth || 1) * (editorView.viewZoom || 1)
    );
    const canvasH = Math.max(
        1,
        (editorView.baseHeight || 1) * (editorView.viewZoom || 1)
    );
    const panX = editorView.viewPan?.x ?? 0;
    const panY = editorView.viewPan?.y ?? 0;
    const vpW = editorView.viewportWidth || canvasW;
    const vpH = editorView.viewportHeight || canvasH;
    const visStartX = Math.max(0, -panX);
    const visEndX = Math.min(canvasW, -panX + vpW);
    const visStartY = Math.max(0, -panY);
    const visEndY = Math.min(canvasH, -panY + vpH);
    const visCanvasW = Math.max(1, visEndX - visStartX);
    const visCanvasH = Math.max(1, visEndY - visStartY);
    const imgW = Math.max(1, editorView.imageWidth || 1);
    const imgH = Math.max(1, editorView.imageHeight || 1);
    const visImgW = visCanvasW * (imgW / canvasW);
    const visImgH = visCanvasH * (imgH / canvasH);
    const fpp = editorView.feetPerPixel;
    const sc = editorView.siteCenterImage ?? { x: imgW / 2, y: imgH / 2 };
    const focusX = editorView.focusImageX ?? imgW / 2;
    const focusY = editorView.focusImageY ?? imgH / 2;
    return {
        halfW: (visImgW / 2) * fpp,
        halfH: (visImgH / 2) * fpp,
        lookX: (focusX - sc.x) * fpp,
        lookZ: layoutPreviewWorldZ((focusY - sc.y) * fpp),
    };
};

/** Set camera distance/target without fighting OrbitControls damping (no euler reset / saveState). */
const applyPreviewCameraOrbitSync = (controls, camera, lookX, lookY, lookZ, distY) => {
    const cam = camera || controls.object;
    syncOrbitControlsLookAt(controls, lookX, lookY, lookZ);
    setVector3Like(cam.position, lookX, distY, lookZ);
    if (typeof controls.update === "function") {
        controls.update();
    }
    return true;
};

/** Apply 2D-matched frustum to orthographic or perspective preview camera. */
const applyLayoutPreviewFrustumToCamera = (controls, camera, frustum, span = 120) => {
    const cam = camera || controls.object;

    if (cam instanceof THREE.OrthographicCamera) {
        syncOrbitControlsLookAt(controls, frustum.lookX, 0, frustum.lookZ);
        cam.left = -frustum.halfW;
        cam.right = frustum.halfW;
        cam.top = frustum.halfH;
        cam.bottom = -frustum.halfH;
        cam.near = 0.1;
        cam.far = Math.max(span * 12, 8000);
        cam.updateProjectionMatrix();
        return applyPreviewCameraOrbitSync(
            controls,
            cam,
            frustum.lookX,
            0,
            frustum.lookZ,
            Math.max(span * 2, 120)
        );
    }

    const fovV = THREE.MathUtils.degToRad(cam.fov ?? 40);
    const aspect = cam.aspect > 0 ? cam.aspect : 1;
    const fovH = 2 * Math.atan(Math.tan(fovV / 2) * aspect);
    const dist = Math.max(
        frustum.halfH / Math.tan(fovV / 2),
        frustum.halfW / Math.tan(fovH / 2),
        40
    );
    return applyPreviewCameraOrbitSync(
        controls,
        cam,
        frustum.lookX,
        0,
        frustum.lookZ,
        dist
    );
};

export const fitLayoutPreviewCamera = (
    controls,
    siteObject,
    camera,
    viewport,
    editorView = null
) => {
    if (!controls?.object || !siteObject) return false;

    const frustum = layoutPreviewFrustumFromEditorView(editorView);
    const box = new THREE.Box3().setFromObject(siteObject);
    if (!frustum && box.isEmpty()) return false;

    const center = new THREE.Vector3();
    const size = new THREE.Vector3();
    if (!box.isEmpty()) {
        box.getCenter(center);
        box.getSize(size);
    }
    const span = Math.max(size.x, size.z, 20);

    if (frustum) {
        return applyLayoutPreviewFrustumToCamera(
            controls,
            camera,
            frustum,
            span
        );
    }

    const look = previewLookAtFromEditorView(center, editorView);
    const cam = camera || controls.object;
    const pad = span * 0.55;
    const aspect =
        viewport?.width > 0 && viewport?.height > 0
            ? viewport.width / viewport.height
            : 1;
    if (cam instanceof THREE.OrthographicCamera) {
        syncOrbitControlsLookAt(controls, look.x, look.y, look.z);
        cam.left = -pad * aspect;
        cam.right = pad * aspect;
        cam.top = pad;
        cam.bottom = -pad;
        cam.near = 0.1;
        cam.far = Math.max(span * 12, 8000);
        cam.updateProjectionMatrix();
    }
    return applyPreviewCameraOrbitSync(
        controls,
        cam,
        look.x,
        look.y,
        look.z,
        look.y + Math.max(span * 2, 120)
    );
};

/** Reset site root offset so preview draft layout is centered at world origin. */
export const resetLayoutPreview3dSiteRoot = (site) => {
    if (!site) return;
    if (site.lastPosition?.set) {
        site.lastPosition.set(0, 0, 0);
    }
    const root = site.siteRef?.[0] ?? site.groupsRef?.[0]?.parent;
    if (root?.position?.set) {
        root.position.set(0, 0, 0);
        root.updateMatrixWorld?.(true);
    }
    site.offset = { x: 0, y: 0, z: 0 };
};

/**
 * Top-down camera on draft layout bbox (after scene graph + cables are mounted).
 * @param {object} site — global.gw.selectedSite
 * @param {object} [draft] — editor draft for footprint fallback
 * @param {{ width, height }} [viewport]
 */
export const fitLayoutPreview3dCamera = (site, draft, viewport, editorView = null) => {
    const controls = site?.orbitControls;
    if (!controls?.object || !site) return false;

    if (!site.layoutPreviewRootReset) {
        resetLayoutPreview3dSiteRoot(site);
        site.layoutPreviewRootReset = true;
    }

    const root = site.groupsRef?.[0]?.parent ?? site.siteRef?.[0];
    if (root) {
        root.updateMatrixWorld?.(true);
        if (fitLayoutPreviewCamera(controls, root, controls.object, viewport, editorView)) {
            return true;
        }
    }

    const pts = layoutDraftFootprintPoints(draft);
    if (!pts.length) return false;

    let sumX = 0;
    let sumZ = 0;
    pts.forEach((p) => {
        sumX += p.x;
        sumZ += p.z;
    });
    const cx = sumX / pts.length;
    const cz = sumZ / pts.length;
    const worldZ = layoutPreviewWorldZ(cz);
    const cam = controls.object;
    const frustum = layoutPreviewFrustumFromEditorView(editorView);
    if (frustum) {
        return applyLayoutPreviewFrustumToCamera(controls, cam, frustum, 120);
    }
    return applyPreviewCameraOrbitSync(controls, cam, cx, 0, worldZ, 240);
};
